import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { ensureThemeJsonArray, getDvSourceImageUrl } from "../../../functions/themeAssets";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

const MIN_BINS = 2;
const MAX_BINS = 5;
const MAX_BIN_NAME = 20;
const MAX_ITEMS_PER_BIN = 15;
const MAX_FILE_MB = 5;
const ICON_SIZE = { width: 400, height: 400 };
const ITEM_SIZE = { width: 200, height: 200 };
const BELT_SIZE = { width: 1938, height: 211 };

const TAB_SETTINGS = "settings";
const TAB_ITEMS = "items";
const FILTER_ALL = "All";
const FILTER_BELT = "Conveyor Belt image";

function parseObject(val) {
  if (val && typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string" && val.trim()) {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    } catch {
      /* not JSON */
    }
  }
  return {};
}

const toBinList = (val) =>
  ensureThemeJsonArray(val)
    .map((v) => String(v ?? "").trim().toUpperCase())
    .filter(Boolean);

const isPng = (file) => (file.name.split(".").pop() || "").toLowerCase() === "png";

/** Resolves true when the image is exactly width x height. */
function hasDimensions(file, { width, height }) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.width === width && img.height === height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    img.src = url;
  });
}

/** Upload-safe copy of a picked file: drops spaces/brackets etc. from the name. */
function renameFileForUpload(file) {
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  return new File([file], `${id}.png`, { type: file.type || "image/png" });
}

/** Pink `code` notes on their own lines, as on the PHP admin. */
function Hints({ lines }) {
  return (
    <div className="bt-hints">
      {lines.map((line) => (
        <code key={line}>{line}</code>
      ))}
    </div>
  );
}

/** "Choose file | Browse" input, like Bootstrap's custom-file. */
function FileField({ inputRef, files, multiple = false, onChange, disabled }) {
  const label = !files.length
    ? "Choose file"
    : files.length === 1
      ? files[0].name
      : `${files.length} files selected`;
  return (
    <label className={`bt-file${disabled ? " is-disabled" : ""}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => onChange(Array.from(e.target.files || []))}
      />
      <span className="bt-file-name">{label}</span>
      <span className="bt-file-browse">Browse</span>
    </label>
  );
}

/** Tag box like the PHP select2 multi-select: × removes a bin, the list below adds one. */
function BinMultiSelect({ options, selected, onAdd, onRemove, disabled }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const available = options.filter((o) => !selected.includes(o));

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="bt-multi" ref={boxRef}>
      <div
        className={`bt-multi-box${open ? " is-open" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!disabled) setOpen((v) => !v);
          }
        }}
      >
        {selected.map((bin) => (
          <span className="bt-tag" key={bin}>
            {bin}
            <button
              type="button"
              className="bt-tag-remove"
              aria-label={`Remove ${bin}`}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(bin);
              }}
            >
              ×
            </button>
          </span>
        ))}
        {!selected.length ? <span className="bt-multi-placeholder">Select bins</span> : null}
      </div>
      {open ? (
        <ul className="bt-multi-menu" role="listbox">
          {available.length ? (
            available.map((bin) => (
              <li key={bin}>
                <button
                  type="button"
                  onClick={() => {
                    onAdd(bin);
                    setOpen(false);
                  }}
                >
                  {bin}
                </button>
              </li>
            ))
          ) : (
            <li className="bt-multi-empty">No more bins to add</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [tab, setTab] = useState(() =>
    window.location.hash === "#tab5" ? TAB_ITEMS : TAB_SETTINGS
  );
  const [tourOpen, setTourOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [newBin, setNewBin] = useState("");
  const [selectedBins, setSelectedBins] = useState([]);
  const [iconBin, setIconBin] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [allIconFile, setAllIconFile] = useState(null);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [itemFiles, setItemFiles] = useState([]);

  const iconInput = useRef(null);
  const allIconInput = useRef(null);
  const itemInput = useRef(null);

  const defaultCat = useMemo(() => toBinList(data?.default_cat), [data]);
  const customCat = useMemo(() => toBinList(data?.custom_cat), [data]);
  const catIcons = useMemo(() => parseObject(data?.cat_icons), [data]);
  const catItems = useMemo(() => parseObject(data?.cat_items), [data]);

  /** Every bin the admin can pick from: active, custom and the theme's original bins. */
  const binOptions = useMemo(
    () => [...new Set([...defaultCat, ...customCat, ...toBinList(data?.full_default_cat)])],
    [defaultCat, customCat, data]
  );

  const itemsFor = (bin) => ensureThemeJsonArray(catItems[bin]).filter(Boolean);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    setSelectedBins(defaultCat);
    if (iconBin && !defaultCat.includes(iconBin)) setIconBin("");
    if (filter !== FILTER_ALL && filter !== FILTER_BELT && !defaultCat.includes(filter)) {
      setFilter(FILTER_ALL);
    }
    // Only re-sync when the saved bins change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCat]);

  useEffect(() => {
    if (!data) return undefined;
    if (!shouldAutoStartThemeUpdateTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = () => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  };

  const showTab = (next) => {
    setTab(next);
    window.history.replaceState(null, "", next === TAB_ITEMS ? "#tab5" : "#tab3");
  };

  const saveFields = async (fields) => {
    await updateThemeData({
      payload: {
        data: fields,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        currentTheme,
      },
      token: adminToken,
    });
    await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
  };

  /** Uploads files to S3 without saving anything; returns their stored filenames in order. */
  const uploadFiles = async (files) => {
    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("uploadOnly", "true");
    const keys = files.map((file, i) => {
      const key = `btimg_${Date.now()}_${i}`;
      formData.append(key, renameFileForUpload(file));
      return key;
    });
    const result = await uploadThemeMedia({ formData, token: adminToken });
    return keys.map((key) => result?.uploaded_files?.[key]).filter(Boolean);
  };

  /** Runs a save, showing PHP-style success/error alerts. */
  const run = async (task, successTitle = "Data Updated") => {
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return false;
    }
    setBusy(true);
    try {
      await task();
      await Swal.fire("Success", successTitle, "success");
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- Game Settings tab ---------------- */

  const addCustomBin = async () => {
    const name = newBin.trim().toUpperCase();
    if (!name) {
      Swal.fire("Error", "Please enter a bin name.", "error");
      return;
    }
    if (name.length > MAX_BIN_NAME) {
      Swal.fire("Error", "Maximum Character length reached", "error");
      return;
    }
    if (binOptions.includes(name)) {
      Swal.fire("Error", "Bin already exist", "error");
      return;
    }
    const ok = await run(() => saveFields({ custom_cat: [...customCat, name] }));
    if (ok) setNewBin("");
  };

  const toggleBin = (bin) => {
    if (selectedBins.includes(bin)) {
      setSelectedBins((prev) => prev.filter((b) => b !== bin));
      return;
    }
    if (selectedBins.length >= MAX_BINS) {
      Swal.fire(
        "Limit reached",
        `Limit reached: the maximum of ${MAX_BINS} bins has been reached.`,
        "warning"
      );
      return;
    }
    setSelectedBins((prev) => [...prev, bin]);
  };

  const saveSelectedBins = () => {
    if (selectedBins.length < MIN_BINS) {
      Swal.fire("Error", `Atleast ${MIN_BINS} bins required`, "error");
      return;
    }
    if (selectedBins.length > MAX_BINS) {
      Swal.fire("Error", `You can have ${MAX_BINS} bins at the most`, "error");
      return;
    }
    run(() => saveFields({ default_cat: selectedBins }));
  };

  /** Shared checks for the 400x400 bin icons. */
  const validateIcon = async (file) => {
    if (!file) return "Please select a file before uploading.";
    if (!isPng(file)) return "Unsupported file format only PNG allowed.";
    if (!(await hasDimensions(file, ICON_SIZE))) {
      return `Icon image size must be ${ICON_SIZE.width} x ${ICON_SIZE.height}.`;
    }
    return null;
  };

  const uploadBinIcon = async (e) => {
    e.preventDefault();
    const error = await validateIcon(iconFile);
    if (error) {
      Swal.fire("Error", error, "error");
      return;
    }
    const ok = await run(async () => {
      const [filename] = await uploadFiles([iconFile]);
      if (!filename) throw new Error("Upload Failed");
      await saveFields({ cat_icons: { ...catIcons, [iconBin]: filename } });
    });
    if (ok) {
      setIconFile(null);
      if (iconInput.current) iconInput.current.value = "";
    }
  };

  const uploadAllIcon = async (e) => {
    e.preventDefault();
    const error = await validateIcon(allIconFile);
    if (error) {
      Swal.fire("Error", error, "error");
      return;
    }
    const ok = await run(async () => {
      const [filename] = await uploadFiles([allIconFile]);
      if (!filename) throw new Error("Failed to upload");
      await saveFields({ all_icon_image: filename });
    });
    if (ok) {
      setAllIconFile(null);
      if (allIconInput.current) allIconInput.current.value = "";
    }
  };

  /* ---------------- Conveyor Belt Item Images tab ---------------- */

  const isBelt = filter === FILTER_BELT;

  const selectFilter = (next) => {
    setFilter(next);
    setItemFiles([]);
    if (itemInput.current) itemInput.current.value = "";
  };

  const uploadItems = async (e) => {
    e.preventDefault();
    const files = itemFiles;
    if (!files.length) {
      Swal.fire("Error", "Please select at least one image to upload.", "error");
      return;
    }
    if (isBelt && files.length > 1) {
      Swal.fire("Error", "Only one Conveyor Belt image allowed.", "error");
      return;
    }
    const existing = isBelt ? [] : itemsFor(filter);
    if (!isBelt && existing.length + files.length > MAX_ITEMS_PER_BIN) {
      Swal.fire(
        "Error",
        `Maximum ${MAX_ITEMS_PER_BIN} images allowed per category. You already have ${existing.length} images and tried to upload ${files.length} more.`,
        "error"
      );
      return;
    }
    const size = isBelt ? BELT_SIZE : ITEM_SIZE;
    for (const file of files) {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        Swal.fire("Error", `Filesize exceeded max limit ${MAX_FILE_MB} Mb (${file.name})`, "error");
        return;
      }
      if (!isPng(file)) {
        Swal.fire("Error", "Unsupported file format only PNG allowed.", "error");
        return;
      }
      if (!(await hasDimensions(file, size))) {
        Swal.fire(
          "Error",
          `Image dimension must be ${size.width}x${size.height} (${file.name})`,
          "error"
        );
        return;
      }
    }

    const ok = await run(async () => {
      const filenames = await uploadFiles(files);
      if (filenames.length !== files.length) throw new Error("Upload failed. Please try again.");
      if (isBelt) {
        await saveFields({ dice_image: filenames[0] });
      } else {
        await saveFields({ cat_items: { ...catItems, [filter]: [...existing, ...filenames] } });
      }
    }, "Successfully Updated");
    if (ok) {
      setItemFiles([]);
      if (itemInput.current) itemInput.current.value = "";
    }
  };

  const deleteItem = async (bin, filename) => {
    const confirm = await Swal.fire({
      title: "Delete this image?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    run(
      () =>
        saveFields({
          cat_items: { ...catItems, [bin]: itemsFor(bin).filter((img) => img !== filename) },
        }),
      "Successfully Deleted"
    );
  };

  const renderItemGrid = (bin) => {
    const items = itemsFor(bin);
    if (!items.length) return <p className="bt-empty">No images uploaded in {bin}.</p>;
    return (
      <div className="bt-item-grid">
        {items.map((img) => (
          <div className="bt-item" key={img}>
            <img src={getDvSourceImageUrl(img)} alt={bin} />
            <button
              type="button"
              className="bt-item-delete"
              onClick={() => deleteItem(bin, img)}
              disabled={busy}
              aria-label="Delete image"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="bt-tabs" data-tour="tour-gc-header">
        <button
          type="button"
          className={`bt-tab${tab === TAB_SETTINGS ? " active" : ""}`}
          onClick={() => showTab(TAB_SETTINGS)}
        >
          <i className="fa-solid fa-square-plus" aria-hidden /> Game Settings
        </button>
        <button
          type="button"
          className={`bt-tab${tab === TAB_ITEMS ? " active" : ""}`}
          onClick={() => showTab(TAB_ITEMS)}
        >
          <i className="fa-solid fa-square-plus" aria-hidden /> Conveyor Belt Item Images
        </button>
      </div>

      {tab === TAB_SETTINGS ? (
        <div className="bt-panel bt-columns" data-tour="tour-gc-settings">
          <section className="bt-column">
            <h4 className="bt-title">Add Custom Bins:</h4>
            <input
              type="text"
              className="bt-input"
              maxLength={MAX_BIN_NAME}
              value={newBin}
              onChange={(e) => setNewBin(e.target.value.replace(/^\s+/, ""))}
            />
            <Hints
              lines={[
                `* You can add upto ${MAX_BINS} bins.`,
                `* Minimum ${MIN_BINS} bins are required.`,
                `* Character limit for the bin name is ${MAX_BIN_NAME}`,
              ]}
            />
            <button type="button" className="bt-btn" onClick={addCustomBin} disabled={busy}>
              Save
            </button>

            <h4 className="bt-title bt-title--spaced">Select / Deselect Bins</h4>
            <BinMultiSelect
              options={binOptions}
              selected={selectedBins}
              onAdd={toggleBin}
              onRemove={toggleBin}
              disabled={busy}
            />
            <button type="button" className="bt-btn" onClick={saveSelectedBins} disabled={busy}>
              Save
            </button>
          </section>

          <section className="bt-column">
            <h4 className="bt-title">Add Bin Image:</h4>
            <select
              className="bt-input bt-select"
              value={iconBin}
              onChange={(e) => {
                setIconBin(e.target.value);
                setIconFile(null);
              }}
            >
              <option value="">Select Bin</option>
              {defaultCat.map((bin) => (
                <option key={bin} value={bin}>
                  {bin}
                </option>
              ))}
            </select>

            {iconBin ? (
              <form onSubmit={uploadBinIcon}>
                <FileField
                  inputRef={iconInput}
                  files={iconFile ? [iconFile] : []}
                  onChange={(files) => setIconFile(files[0] || null)}
                  disabled={busy}
                />
                <Hints
                  lines={[
                    "* Allowed Photo Formats - PNG",
                    `* image size must be ${ICON_SIZE.width} x ${ICON_SIZE.height}`,
                  ]}
                />
                {catIcons[iconBin] ? (
                  <img
                    className="bt-icon-preview"
                    src={getDvSourceImageUrl(catIcons[iconBin])}
                    alt={iconBin}
                  />
                ) : null}
                <button type="submit" className="bt-btn" disabled={busy}>
                  <i className="fa-solid fa-cloud-arrow-up" aria-hidden /> Upload
                </button>
              </form>
            ) : null}

            <h4 className="bt-title bt-title--spaced">Add Default Bin Icon</h4>
            <form onSubmit={uploadAllIcon}>
              <FileField
                inputRef={allIconInput}
                files={allIconFile ? [allIconFile] : []}
                onChange={(files) => setAllIconFile(files[0] || null)}
                disabled={busy}
              />
              <Hints
                lines={[
                  "* Allowed Photo Formats - PNG",
                  `* Icon image size must be ${ICON_SIZE.width}x${ICON_SIZE.height}`,
                ]}
              />
              {data?.all_icon_image ? (
                <img
                  className="bt-icon-preview"
                  src={getDvSourceImageUrl(data.all_icon_image)}
                  alt="Default bin icon"
                />
              ) : null}
              <button type="submit" className="bt-btn" disabled={busy}>
                Upload
              </button>
            </form>
          </section>
        </div>
      ) : (
        <div className="bt-panel" data-tour="tour-gc-settings">
          <h4 className="bt-title">Add Game Image</h4>
          <Hints lines={["*Click a bin name to add images."]} />

          <div className="bt-filters">
            {[FILTER_ALL, ...defaultCat, FILTER_BELT].map((name) => (
              <button
                key={name}
                type="button"
                className={`bt-filter${filter === name ? " active" : ""}`}
                onClick={() => selectFilter(name)}
              >
                {name}
              </button>
            ))}
          </div>

          {filter === FILTER_ALL ? (
            defaultCat.length ? (
              defaultCat.map((bin) => (
                <div className="bt-bin-group" key={bin}>
                  <h5 className="bt-subtitle">{bin}</h5>
                  {renderItemGrid(bin)}
                </div>
              ))
            ) : (
              <p className="bt-empty">No images uploaded yet.</p>
            )
          ) : (
            <form onSubmit={uploadItems}>
              <h4 className="bt-title">Add Main Image</h4>
              <FileField
                inputRef={itemInput}
                files={itemFiles}
                multiple={!isBelt}
                onChange={setItemFiles}
                disabled={busy}
              />
              <Hints
                lines={
                  isBelt
                    ? [
                        "* Allowed photo format: PNG.",
                        "* Only 1 image is allowed for Conveyor Belt.",
                        `* Image dimensions: ${BELT_SIZE.width} x ${BELT_SIZE.height} px.`,
                      ]
                    : [
                        "* Allowed photo format: PNG.",
                        `* You can add up to ${MAX_ITEMS_PER_BIN} images for each bin.`,
                        `* Image dimensions: ${ITEM_SIZE.width} x ${ITEM_SIZE.height} px.`,
                      ]
                }
              />
              <button type="submit" className="bt-btn" disabled={busy}>
                <i className="fa-solid fa-cloud-arrow-up" aria-hidden />{" "}
                {busy ? "Uploading..." : "Upload"}
              </button>

              <div className="bt-preview">
                {isBelt ? (
                  data?.dice_image ? (
                    <img
                      className="bt-belt-preview"
                      src={getDvSourceImageUrl(data.dice_image)}
                      alt="Conveyor belt"
                    />
                  ) : (
                    <p className="bt-empty">No Conveyor Belt image uploaded yet.</p>
                  )
                ) : (
                  renderItemGrid(filter)
                )}
              </div>
            </form>
          )}
        </div>
      )}

      <ThemeUpdateFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default ThemeUpdate;
